import React, { useState, useEffect, useContext, useCallback } from "react";
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import ContractContext from '../../ContractContext';
import { Dialog } from 'primereact/dialog';
import { formatCurrencyExtended, formatDecimals, } from '../../utils';
import { Switch } from "@nextui-org/react";

const TokenSelectorModal = React.memo(({ tokens, onSelect, isVisible, setShowModal, getTokenLogoUrl, formatBalanceWithLogo, poolIndex, poolType }) => {

    const contractContext = useContext(ContractContext);
    const contracts = contractContext.contracts;

    const [searchTerm, setSearchTerm] = useState('');
    const [viewOnlyTokensWithBalance, setViewOnlyTokensWithBalance] = useState(() => {
        const cachedView = localStorage.getItem("viewOnlyTokensWithBalance");
        return cachedView ? JSON.parse(cachedView) : true;
    });

    const [viewRewardsAvailable, setViewRewardsAvailable] = useState(() => {
        const cachedView = localStorage.getItem("viewRewardsAvailable");
        return cachedView ? JSON.parse(cachedView) : false;
    });

    let filteredTokens = tokens.filter(token => {
        const tokenHasBalance = parseFloat(token.balanceOfHolder) > 0 && (parseFloat(token.balanceOfHolder) * parseFloat(token.price) / (10 ** token.decimals)) > 0.009;
        const matchesSearchTerm = token.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
            token.value.toLowerCase().includes(searchTerm.toLowerCase()) ||
            token.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            token.address.toLowerCase().includes(searchTerm.toLowerCase());

        return viewOnlyTokensWithBalance ? tokenHasBalance && matchesSearchTerm : matchesSearchTerm;
    }).sort((a, b) =>
        viewOnlyTokensWithBalance ?
            (parseFloat(b.balanceOfHolder) * parseFloat(b.price) / (10 ** b.decimals)) - (parseFloat(a.balanceOfHolder) * parseFloat(a.price) / (10 ** a.decimals))
            : (b.rocketFiSubscribed - a.rocketFiSubscribed));

    useEffect(() => {
        // Update localStorage whenever viewOnlyTokensWithBalance changes
        localStorage.setItem("viewOnlyTokensWithBalance", JSON.stringify(viewOnlyTokensWithBalance));
    }, [viewOnlyTokensWithBalance]);

    useEffect(() => {
        // Update localStorage whenever viewRewardsAvailable changes
        localStorage.setItem("viewRewardsAvailable", JSON.stringify(viewRewardsAvailable));
    }, [viewRewardsAvailable]);

    const footerContent = () => (
        <div style={{
            justifyContent: 'flex-end', alignItems: 'center', display: 'flex', gap: '5px', flexDirection: 'row',
            alignContent: 'center',
        }}>
            <div>
                <Switch
                    checked={viewRewardsAvailable}
                    onChange={() => setViewRewardsAvailable(!viewRewardsAvailable)}
                    size="xs"
                />
            </div>
            <div style={{ fontSize: '12px' }}>
                {viewRewardsAvailable ? 'Floatie View On' : 'Floatie View Off'}
            </div>
            <div>
                <Switch
                    checked={viewOnlyTokensWithBalance}
                    onChange={() => setViewOnlyTokensWithBalance(!viewOnlyTokensWithBalance)}
                    size="xs"
                />
            </div>
            <div style={{ fontSize: '12px' }}>
                {viewOnlyTokensWithBalance ? 'Wallet View On' : 'Wallet View Off'}
            </div>
        </div>
    );

    return (
        <Dialog
            header="Select a Token"
            visible={isVisible}
            style={{
                width: '95%', maxWidth: '450px', minWidth: '300px',
                height: '500px',
                maxHeight: '600px', overflow: 'auto',
                borderRadius: '25px',
            }}  // Adjust width as needed
            onHide={() => setShowModal(false)}
            footer={footerContent}
        >
            <div>
                <InputText
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search name, symbol or paste address"
                    style={{ width: '100%', marginBottom: '10px', borderRadius: '25px' }}
                />

                <div style={{
                    display: 'flex', flexDirection: 'column', gap: '2px',
                    alignContent: 'center', justifyContent: 'center', textAlign: 'center',
                    alignItems: 'center', borderRadius: '25px',
                }}>
                    {filteredTokens.map(token => (
                        <Button
                            key={token.value}
                            onClick={() => onSelect(token)}
                            outlined
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center', // Center the contents
                                gap: '2px',
                                width: '100%', // Minimum width for each button
                                borderRadius: '5px',
                                border: 'none',
                                color: 'black',
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                <div style={{
                                    justifyContent: 'center', alignItems: 'center', display: 'flex', gap: '5px', flexDirection: 'row',
                                }}>
                                    <img src={getTokenLogoUrl(token)} alt={token.label} style={{ width: '30px' }} />
                                    <div style={{
                                        justifyContent: 'left', alignItems: 'flex-start', display: 'flex', flexDirection: 'column',
                                    }}>
                                        <div>
                                            <strong> {token.label}</strong>
                                        </div>
                                        <div style={{ fontSize: '12px' }}>
                                            {token.name}
                                        </div>
                                    </div>
                                </div>

                                <div style={{
                                    justifyContent: 'right', alignItems: 'flex-end', display: 'flex', flexDirection: 'column',
                                }}>
                                    {
                                        viewRewardsAvailable ? <RewardsCardSmall token={token} formatBalanceWithLogo={formatBalanceWithLogo} poolIndex={poolIndex} poolType={poolType} />
                                            :
                                            <>
                                                <div>
                                                    <strong> {(formatDecimals(token.balanceOfHolder, token.decimals))} </strong>
                                                </div>
                                                <div style={{ fontSize: '12px' }}>
                                                    {(' ($' + (parseFloat(token.balanceOfHolder) * parseFloat(token.price) / (10 ** token.decimals)).toFixed(2) + ')')}
                                                </div>
                                            </>
                                    }
                                </div>
                            </div>
                        </Button>
                    ))}
                </div>
            </div>
        </Dialog>
    );
});

const RewardsCardSmall = React.memo(({ token, formatBalanceWithLogo, poolIndex, poolType }) => {
    const [rewardsAvailable, setRewardsAvailable] = useState(0);
    const [rewardsAvailableUSD, setRewardsAvailableUSD] = useState(0);
    const [rewardRate, setRewardRate] = useState(0);
    const [adjustedRewardsAvailable, setAdjustedRewardsAvailable] = useState(0);
    const [adjustedRewardsAvailableUSD, setAdjustedRewardsAvailableUSD] = useState(0);

    const [requiredAmount, setRequiredAmount] = useState(0);
    const [requiredAmountUSD, setRequiredAmountUSD] = useState(0);
    const { contracts } = useContext(ContractContext);

    useEffect(() => {
        const getRewardsAvailable = async () => {
            if (token) {
                await contracts.floatiePlay.methods.playPlatform(token.address, poolIndex).call().then(setRewardsAvailable);
            }
        }

        if (token) {
            getRewardsAvailable();
        }
    }, [token, contracts?.floatiePlay, poolIndex]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (token) {
                contracts.floatiePlay.methods.playPlatform(token.address, poolIndex).call().then(setRewardsAvailable);
            }
        }, 5000);
        return () => clearInterval(interval);
    }, [token, contracts?.floatiePlay, poolIndex]);

    useEffect(() => {
        if (rewardsAvailable && token) {
            setRewardsAvailableUSD(parseFloat(rewardsAvailable.total) * parseFloat(token.price) / (10 ** token.decimals));
            const accuracy = parseFloat(rewardsAvailable.poolSettings.accuracy) <= parseFloat(rewardsAvailable.poolSettings.chance) ? 10000 : parseFloat(rewardsAvailable.poolSettings.accuracy);
            setRewardRate(parseFloat(rewardsAvailable.poolSettings.rewardRate) / accuracy);
            setRequiredAmount(parseFloat(rewardsAvailable.poolSettings.requiredAmount));
            setRequiredAmountUSD(parseFloat(rewardsAvailable.poolSettings.requiredAmount) * parseFloat(token.price) / (10 ** token.decimals));
        }
    }, [token, rewardsAvailable]);

    useEffect(() => {
        if (rewardsAvailable && rewardRate) {
            const adjustedRewardValue = (parseFloat(rewardsAvailable.total) * rewardRate).toFixed(0);
            setAdjustedRewardsAvailable(Math.min(adjustedRewardValue, rewardsAvailable.total));
            setAdjustedRewardsAvailableUSD((Math.min(adjustedRewardValue, rewardsAvailable.total) * parseFloat(token.price) / (10 ** token.decimals)).toFixed(2));
        }
    }, [rewardsAvailable, rewardRate]);

    return (
        <>
            {adjustedRewardsAvailableUSD > 0.009 &&
                <div style={{
                    justifyContent: 'center', alignItems: 'center', display: 'flex', gap: '5px', flexDirection: 'row', marginTop: '2px', marginBottom: '2px',
                }} >
                    <div style={{
                        paddingBottom: '4px', paddingTop: '4px', paddingLeft: '8px', paddingRight: '8px', borderRadius: '10px', color: 'white', backgroundColor: 'black',
                    }}>
                        <div style={{ justifyContent: 'center', alignItems: 'center', display: 'flex', gap: '5px', flexDirection: 'row', width: '100%', fontSize: '10px', fontWeight: 'thin' }}>
                            Rewards Available
                        </div>
                        <div style={{ justifyContent: 'center', alignItems: 'center', display: 'flex', gap: '5px', flexDirection: 'row', width: '100%', fontSize: '14px', fontWeight: 'bold' }}>
                            {formatBalanceWithLogo(parseFloat(adjustedRewardsAvailable), token)}
                        </div>
                        <div style={{ justifyContent: 'center', alignItems: 'center', display: 'flex', gap: '5px', flexDirection: 'row', width: '100%', fontSize: '12px', fontWeight: 'thin' }}>
                            {formatCurrencyExtended(parseFloat(adjustedRewardsAvailableUSD))}
                        </div>
                    </div>
                    <div style={{
                        paddingBottom: '4px', paddingTop: '4px', paddingLeft: '8px', paddingRight: '8px', borderRadius: '10px', color: 'black', backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    }}>
                        <div style={{ justifyContent: 'center', alignItems: 'center', display: 'flex', gap: '5px', flexDirection: 'row', width: '100%', fontSize: '10px', fontWeight: 'thin' }}>
                            Min. {poolType} Required
                        </div>
                        <div style={{ justifyContent: 'center', alignItems: 'center', display: 'flex', gap: '5px', flexDirection: 'row', width: '100%', fontSize: '14px', fontWeight: 'bold' }}>
                            {formatBalanceWithLogo(parseFloat(requiredAmount), token)}
                        </div>
                        <div style={{ justifyContent: 'center', alignItems: 'center', display: 'flex', gap: '5px', flexDirection: 'row', width: '100%', fontSize: '12px', fontWeight: 'thin' }}>
                            {formatCurrencyExtended(parseFloat(requiredAmountUSD))}
                        </div>
                    </div>
                </div >
            }
        </>
    );
});

export default TokenSelectorModal;